<template>
    <aside class="seo-panel" :class="{ 'seo-panel--collapsed': collapsedInternal }" aria-label="Análise de SEO">
        <header class="seo-panel__header">
            <button
                type="button"
                class="seo-panel__toggle"
                :title="collapsedInternal ? 'Expandir painel SEO' : 'Recolher painel SEO'"
                @click="toggle"
            >
                <v-icon small>{{ collapsedInternal ? 'mdi-chevron-left' : 'mdi-chevron-right' }}</v-icon>
            </button>

            <div v-if="!collapsedInternal" class="seo-panel__heading">
                <span class="seo-panel__eyebrow">Análise de SEO</span>
                <span class="seo-panel__heading-title">Score do artigo</span>
            </div>
        </header>

        <!-- COLLAPSED VIEW -->
        <div v-if="collapsedInternal" class="seo-panel__rail">
            <div class="seo-rail-score" :class="`seo-rail-score--${colorClass}`" :title="`SEO ${score}/100`">
                <span class="seo-rail-score__value">{{ score }}</span>
            </div>
            <div class="seo-rail-counts">
                <span class="seo-rail-count seo-rail-count--good" :title="`${counts.good} ok`">
                    <v-icon x-small color="success">mdi-check</v-icon>
                    {{ counts.good }}
                </span>
                <span class="seo-rail-count seo-rail-count--ok" :title="`${counts.ok} aviso(s)`">
                    <v-icon x-small color="warning">mdi-alert</v-icon>
                    {{ counts.ok }}
                </span>
                <span class="seo-rail-count seo-rail-count--bad" :title="`${counts.bad} problema(s)`">
                    <v-icon x-small color="error">mdi-close</v-icon>
                    {{ counts.bad }}
                </span>
            </div>
        </div>

        <!-- EXPANDED VIEW -->
        <div v-else class="seo-panel__body">
            <div class="seo-score" :class="`seo-score--${colorClass}`">
                <div class="seo-score__circle" :style="circleStyle" role="img" :aria-label="`SEO score ${score}`">
                    <div class="seo-score__inner">
                        <span class="seo-score__value">{{ score }}</span>
                        <span class="seo-score__suffix">/100</span>
                    </div>
                </div>
                <div class="seo-score__meta">
                    <span class="seo-score__label">{{ scoreLabel }}</span>
                    <span class="seo-score__hint mercury--text">
                        {{ counts.good }} OK · {{ counts.ok }} aviso{{ counts.ok === 1 ? '' : 's' }} · {{ counts.bad }} problema{{ counts.bad === 1 ? '' : 's' }}
                    </span>
                </div>
            </div>

            <ul class="seo-checks">
                <li
                    v-for="check in checks"
                    :key="check.id"
                    class="seo-check"
                    :class="[
                        `seo-check--${check.status}`,
                        { 'seo-check--open': openChecks[check.id] },
                    ]"
                >
                    <button
                        type="button"
                        class="seo-check__row"
                        :aria-expanded="!!openChecks[check.id]"
                        @click="toggleCheck(check.id)"
                    >
                        <span class="seo-check__icon">
                            <v-icon small :color="iconColor(check.status)">{{ iconName(check.status) }}</v-icon>
                        </span>
                        <span class="seo-check__label">{{ check.label }}</span>
                        <v-icon x-small class="seo-check__chevron">mdi-chevron-down</v-icon>
                    </button>

                    <transition name="seo-check-fade">
                        <div v-if="openChecks[check.id]" class="seo-check__detail">
                            <p class="seo-check__message ma-0">{{ check.message }}</p>
                            <p v-if="check.tip" class="seo-check__tip mercury--text ma-0 mt-2">
                                <v-icon x-small color="mercury" class="mr-1">mdi-lightbulb-outline</v-icon>
                                {{ check.tip }}
                            </p>
                        </div>
                    </transition>
                </li>
            </ul>

        </div>
    </aside>
</template>

<script>
import { computeSeoScore, seoScoreColor, seoScoreLabel } from '@/lib/seoScore'

const STORAGE_KEY = 'artigos.seoPanel.collapsed'

export default {
    name: 'ArtigosSeoPanel',
    props: {
        html: { type: String, default: '' },
        title: { type: String, default: '' },
        metaDescription: { type: String, default: '' },
        focusKeyword: { type: String, default: '' },
        defaultCollapsed: { type: Boolean, default: false },
    },
    data() {
        return {
            collapsedInternal: this.defaultCollapsed,
            openChecks: {},
        }
    },
    computed: {
        result() {
            return computeSeoScore({
                html: this.html,
                title: this.title,
                metaDescription: this.metaDescription,
                focusKeyword: this.focusKeyword,
            })
        },
        score() {
            return this.result.score
        },
        checks() {
            return this.result.checks
        },
        counts() {
            const acc = { good: 0, ok: 0, bad: 0, na: 0 }
            for (const c of this.checks) acc[c.status] = (acc[c.status] || 0) + 1
            return acc
        },
        colorClass() {
            const c = seoScoreColor(this.score)
            return c === 'success' ? 'good' : c === 'warning' ? 'ok' : c === 'error' ? 'bad' : 'na'
        },
        scoreLabel() {
            return seoScoreLabel(this.score)
        },
        circleStyle() {
            const angle = Math.max(0, Math.min(100, this.score)) * 3.6
            const colorVar = this.colorClass === 'good'
                ? 'var(--seo-good)'
                : this.colorClass === 'ok'
                    ? 'var(--seo-ok)'
                    : 'var(--seo-bad)'
            return {
                background: `conic-gradient(${colorVar} ${angle}deg, var(--seo-track) ${angle}deg 360deg)`,
            }
        },
    },
    watch: {
        // Auto-expande primeiro item bad/ok quando o conjunto muda significativamente
        score: {
            immediate: false,
            handler() {
                // intencional: não força abertura — deixa o usuário controlar
            },
        },
    },
    mounted() {
        try {
            const stored = window.localStorage.getItem(STORAGE_KEY)
            if (stored === 'true') this.collapsedInternal = true
            else if (stored === 'false') this.collapsedInternal = false
        } catch (_) {
            // ignore
        }
    },
    methods: {
        toggle() {
            this.collapsedInternal = !this.collapsedInternal
            try {
                window.localStorage.setItem(STORAGE_KEY, String(this.collapsedInternal))
            } catch (_) {
                // ignore
            }
            this.$emit('toggle', this.collapsedInternal)
        },
        toggleCheck(id) {
            this.$set(this.openChecks, id, !this.openChecks[id])
        },
        iconName(status) {
            if (status === 'good') return 'mdi-check-circle'
            if (status === 'ok') return 'mdi-alert-circle'
            if (status === 'bad') return 'mdi-close-circle'
            return 'mdi-minus-circle-outline'
        },
        iconColor(status) {
            if (status === 'good') return 'success'
            if (status === 'ok') return 'warning'
            if (status === 'bad') return 'error'
            return 'mercury'
        },
    },
}
</script>

<style scoped>
.seo-panel {
    --seo-good: #22c55e;
    --seo-ok: #f59e0b;
    --seo-bad: #ef4444;
    --seo-track: var(--glass-soft-bg);
    --seo-border: var(--glass-panel-border);

    position: sticky;
    top: 16px;
    align-self: flex-start;
    width: 320px;
    max-height: calc(100vh - 200px);
    background: var(--glass-panel-bg);
    -webkit-backdrop-filter: blur(16px);
    backdrop-filter: blur(16px);
    border: 1px solid var(--glass-panel-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-glass);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: width 220ms cubic-bezier(0.22, 0.61, 0.36, 1);
}

.seo-panel--collapsed {
    width: 64px;
}

.seo-panel__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--seo-border);
    background: var(--glass-soft-bg);
}

.seo-panel__toggle {
    width: 28px;
    height: 28px;
    border-radius: var(--radius-xs);
    border: 1px solid var(--seo-border);
    background: var(--color-surface-container-lowest);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 120ms;
    color: var(--color-on-surface);
}

.seo-panel__toggle:hover {
    background: var(--color-surface-container-low);
}

.seo-panel__heading {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
}

.seo-panel__eyebrow {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: var(--color-on-surface-variant);
    font-weight: 600;
}

.seo-panel__heading-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-on-surface);
}

.seo-panel__body {
    overflow-y: auto;
    padding: 16px 14px 12px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.seo-score {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 6px 4px 10px;
    border-bottom: 1px dashed var(--seo-border);
}

.seo-score__circle {
    --size: 80px;
    width: var(--size);
    height: var(--size);
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.seo-score__inner {
    width: calc(100% - 12px);
    height: calc(100% - 12px);
    background: var(--color-surface-container-lowest);
    border-radius: 50%;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1;
}

.seo-score__value {
    font-size: 22px;
    font-weight: 700;
    color: var(--color-on-surface);
}

.seo-score__suffix {
    font-size: 10px;
    color: var(--color-on-surface-variant);
    margin-top: 2px;
}

.seo-score__meta {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}

.seo-score__label {
    font-weight: 600;
    color: var(--color-on-surface);
    font-size: 14px;
}

.seo-score--good .seo-score__label { color: var(--seo-good); }
.seo-score--ok .seo-score__label { color: #b45309; }
.seo-score--bad .seo-score__label { color: var(--seo-bad); }

.seo-score__hint {
    font-size: 11px;
}

.seo-checks {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.seo-check {
    border: 1px solid transparent;
    border-radius: var(--radius-sm);
    transition: background 120ms, border-color 120ms;
}

.seo-check:hover {
    background: var(--glass-soft-bg);
}

.seo-check--open {
    background: var(--glass-soft-bg);
    border-color: var(--seo-border);
}

.seo-check__row {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: 0;
    background: transparent;
    cursor: pointer;
    text-align: left;
    color: var(--color-on-surface);
    font-size: 12.5px;
    line-height: 1.3;
    font-family: inherit;
}

.seo-check__icon {
    flex-shrink: 0;
    width: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.seo-check__label {
    flex: 1;
}

.seo-check__chevron {
    transition: transform 160ms;
    color: var(--color-on-surface-variant);
}

.seo-check--open .seo-check__chevron {
    transform: rotate(180deg);
}

.seo-check--na .seo-check__label {
    color: var(--color-on-surface-variant);
}

.seo-check__detail {
    padding: 0 12px 10px 40px;
}

.seo-check__message {
    font-size: 12px;
    color: var(--color-on-surface-variant);
}

.seo-check__tip {
    font-size: 11.5px;
    line-height: 1.45;
    display: flex;
    align-items: flex-start;
    gap: 4px;
}

.seo-check-fade-enter-active,
.seo-check-fade-leave-active {
    transition: opacity 140ms, transform 140ms;
}
.seo-check-fade-enter,
.seo-check-fade-leave-to {
    opacity: 0;
    transform: translateY(-2px);
}


/* Collapsed rail view */
.seo-panel__rail {
    padding: 14px 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.seo-rail-score {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 700;
    font-size: 14px;
}

.seo-rail-score--good { background: var(--seo-good); }
.seo-rail-score--ok { background: var(--seo-ok); }
.seo-rail-score--bad { background: var(--seo-bad); }
.seo-rail-score--na { background: var(--color-outline-variant); }

.seo-rail-counts {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: center;
}

.seo-rail-count {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 11px;
    color: var(--color-on-surface-variant);
    font-weight: 600;
}

@media (max-width: 1100px) {
    .seo-panel { width: 280px; }
    .seo-panel--collapsed { width: 56px; }
    .seo-score__circle { --size: 70px; }
    .seo-score__value { font-size: 20px; }
}
</style>
